import React from "react";

import { css } from "@emotion/core";

import { Dropdown } from "@zapier/design-system";
import { useTrackingContext } from "@zapier/toolbox-tracking";

import { UseQuery } from "@zapier/checkout/esm/types";
import { sortCurrenciesByArray } from "@zapier/checkout/esm/utils";
import { Currency, defaultCurrency, useCurrencies } from "@zapier/currency";

import { FlagIcon } from "./FlagIcon";
import { PLAN_CHANGE_EVENT, currencyOrder } from "./constants";

// Rebrand styles
const containerCss = css`
  width: 160px;
  text-align: left;
  @media (max-width: 375px) {
      width: 100%;
    }
  }
`;

type Props = {
  onSelectCurrency: (currency: Currency) => void;
  selectedCurrency: Currency;
  useQuery: UseQuery;
};

/**
 * A dropdown that allows the user to select a currency to pay in.
 */
export const CurrencyDropdown = ({
  onSelectCurrency,
  selectedCurrency = defaultCurrency,
  useQuery,
  ...restProps
}: Props) => {
  const { trackEvent } = useTrackingContext();
  const { isLoading, currencies: options, error } = useCurrencies({ useQuery });

  const onChangeCurrency = (currency: Currency) => {
    onSelectCurrency(currency);
    // Send event with currency
    trackEvent(PLAN_CHANGE_EVENT, {
      absolute_url: window.location.href,
      event_action: "select_currency",
      interaction_goal: "select currency",
      interaction_name: "currency dropdown",
      option_selected: currency.name,
    });
  };

  // Don't show if loading or error
  if (isLoading || error) {
    return null;
  }

  return (
    <div css={containerCss} {...restProps}>
      <Dropdown
        ariaLabel="Currency"
        getLabelForItem={item => `${item.name} ${item.symbol}`}
        items={[...options].sort((a: Currency, b: Currency) =>
          sortCurrenciesByArray(a, b, currencyOrder)
        )}
        menuAriaLabel="List of currencies"
        onChange={(currency: Currency) => onChangeCurrency(currency)}
        placeholder="Choose a currency..."
        renderIcon={item => {
          //@ts-ignore
          return !!item && <FlagIcon name={item.icon} />;
        }}
        // Pull this custom code up from the checkout's Dropdown component
        {...(selectedCurrency
          ? { selectedItem: selectedCurrency }
          : { initialSelectedItem: selectedCurrency })}
      />
    </div>
  );
};
